import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { getExtractionDataIcon } from '~/common/componentMapper/QuestionExtraction/handlers';
import Flex from '~/common/components/Flex';
import FormLabel from '~/common/components/Forms/FormLabel';
import theme from '~/theme/';

import QuestionLibraryButton from '../../components/buttons/QuestionLibraryButton';
import ConditionEvaluator from '../../components/utilityComponents/ConditionEvaluator';
import ProcessedText from '../../components/utilityComponents/ProcessedText';
import {
  useQuestionLibraryActions,
  useQuestionLibraryStore,
} from '../../store/questionLibraryStore';
import { evaluateCondition } from '../../utils/utils';

const isInArray = (name: string): number => {
  const match = name.match(/\.(\d+)\./); // Match a dot, followed by digits, followed by another dot
  const index = match ? Number.parseInt(match[1], 10) : null;
  if (index !== null) {
    return index;
  }
  return null;
};

const formatAutofillFields = (
  inputName: string,
  autofillFields: { fieldKey: string; optionKey: string; override: any }[],
) => {
  const position = isInArray(inputName);
  if (position !== null) {
    return autofillFields.map((autofill) => ({
      ...autofill,
      fieldKey: autofill.fieldKey.replace(`{i}`, position.toString()),
    }));
  }
  return autofillFields;
};
export default function Autofill({ question }) {
  const {
    name,
    autoFills,
    label = 'Available Autofill(s)',
    optionLabelKey,
    optionValueKey,
    autoFillFields = [],
    placeholders = [],
    conditions = {},
    metadata = {},
  } = question;
  const autoFillOptions = useQuestionLibraryStore(
    (s) => s.screenData[autoFills.key],
  );
  const questionAnswers = useQuestionLibraryStore(
    (s) => s.screenData.questionAnswers,
  );
  const screenData = useQuestionLibraryStore((s) => s.screenData);
  const { updateScreenDataKey } = useQuestionLibraryActions();
  const { setValue, getValues, control } = useFormContext();
  const { field } = useController({
    name,
    control,
    rules: {},
    defaultValue: '',
  });
  const options = useMemo(() => {
    if (Array.isArray(autoFillOptions)) {
      return autoFillOptions;
    }
    return [];
  }, [autoFillOptions]);

  const formattedAutofillFields = useMemo(
    () => formatAutofillFields(name, autoFillFields),
    [name, autoFillFields],
  );

  if (options.length === 0) {
    return null;
  }

  const handleAutoFillOptionSelection = (option, index) => {
    if (field.value === option[optionValueKey]) {
      handleClear();
    } else {
      if (field.value !== '') {
        const previousOption = options.find(
          (opt) => opt[optionValueKey] === field.value,
        );
        updateScreenDataKey(
          `${autoFills.key}[${options.indexOf(previousOption)}].isDisabled`,
          false,
        );
      }
      setValue(name, option[optionValueKey]);
      handleAutoFill(option);
    }
    updateScreenDataKey(
      `${autoFills.key}[${index}].isDisabled`,
      !option.isDisabled,
    );
  };

  const handleAutoFill = (option) => {
    const newQuestionAnswers = structuredClone(questionAnswers);
    formattedAutofillFields.forEach((autofill) => {
      let canOverride = true;
      if (autofill.override && Object.keys(autofill.override).length > 0) {
        canOverride = evaluateCondition(
          autofill.override,
          getValues(),
          screenData,
          metadata,
        );
      }
      if (canOverride) {
        newQuestionAnswers[autofill.fieldKey] =
          option.questionAnswers[autofill.optionKey];
        setValue(autofill.fieldKey, option[autofill.optionKey]);
      }
    });

    updateScreenDataKey('questionAnswers', newQuestionAnswers);
  };

  const handleClear = () => {
    const previousOption = options.find(
      (opt) => opt[optionValueKey] === field.value,
    );
    updateScreenDataKey(
      `${autoFills.key}[${options.indexOf(previousOption)}].isDisabled`,
      false,
    );
    setValue(name, '');
    const newQuestionAnswers = structuredClone(questionAnswers);
    formattedAutofillFields.forEach((autofill) => {
      let canOverride = true;
      if (autofill.override && Object.keys(autofill.override).length > 0) {
        canOverride = evaluateCondition(
          autofill.override,
          getValues(),
          screenData,
          metadata,
        );
      }
      if (canOverride) {
        newQuestionAnswers[autofill.fieldKey] = '';
        setValue(autofill.fieldKey, '');
      }
    });
    updateScreenDataKey('questionAnswers', newQuestionAnswers);
  };

  return (
    <ConditionEvaluator conditions={conditions}>
      <Flex alignItems={'center'} flexWrap={'wrap'}>
        <Flex alignItems={'center'}>
          <FontAwesomeIcon
            icon={getExtractionDataIcon(1, '') as IconProp}
            color={theme.colors.blue[600]}
          />
          <FormLabel
            display={'inline'}
            marginInlineEnd={0}
            htmlFor={name}
            margin={'auto'}
            marginLeft={'0.5rem'}
          >
            <ProcessedText
              text={label}
              placeholders={placeholders}
              name={'autofill-label'}
            />
          </FormLabel>
        </Flex>
        <Flex paddingLeft={'0.25rem'} paddingTop={'0'}>
          {options.map((option, i) => (
            <AutofillButton
              key={option[optionValueKey]}
              label={option[optionLabelKey]}
              isSelected={field.value === option[optionValueKey]}
              isDisabled={
                option.isDisabled && field.value !== option[optionValueKey]
              }
              handleClick={() => handleAutoFillOptionSelection(option, i)}
            />
          ))}
          {field.value && (
            <QuestionLibraryButton
              handleClick={() => handleClear()}
              label={'Clear'}
              fontColor={theme.colors.blue[600]}
              backgroundColor={theme.colors.white}
              testId={'autofill-clear-button'}
              variant={'default'}
              sx={{
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: theme.colors.blue[600],
                marginLeft: '0.5rem',
              }}
            />
          )}
        </Flex>
      </Flex>
    </ConditionEvaluator>
  );
}

type AutofillButtonProps = {
  handleClick: () => void;
  label: string;
  isSelected: boolean;
  isDisabled: boolean;
  id?: string;
};

function AutofillButton({
  handleClick,
  label,
  isSelected,
  isDisabled,
  id,
}: Readonly<AutofillButtonProps>) {
  const styles = useMemo(
    () => ({
      background: isSelected ? theme.colors.blue[600] : 'none',
      color: isSelected ? theme.colors.white : theme.colors.blue[600],
    }),
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [isSelected, isDisabled],
  );
  return (
    <QuestionLibraryButton
      handleClick={() => handleClick()}
      label={label}
      isDisabled={isDisabled}
      fontColor={styles.color}
      backgroundColor={styles.background}
      testId={`autofill-button-${id}`}
      variant={'default'}
      sx={{
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: isSelected ? 'transparent' : theme.colors.blue[600],
        marginLeft: '0.5rem',
      }}
    />
  );
}
